import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { INVESTOWN_CURRENCY } from '@app/common';
import { ValueAmount } from '../../../value/amount/value-amount';
import { IconInvestown } from '../../../icons/investown';
import { Tooltip } from '../../../tooltip/tooltip';

type AssetMiniInvestownType = {
    investownTotalValue: number;
    investownTotalValueSource: number;
    numberOfInvestownAssets: number;
    currencyCode: string;
    showConvertedValue: boolean;
    onToggleValueDisplay?: () => void;
};

export const AssetMiniInvestown: React.FC<AssetMiniInvestownType> = observer(
    ({
        investownTotalValue,
        investownTotalValueSource,
        numberOfInvestownAssets,
        currencyCode,
        showConvertedValue,
        onToggleValueDisplay,
    }) => {
        const { t } = useTranslation();

        if (numberOfInvestownAssets === 0) {
            return null;
        }

        const isSameCurrency = currencyCode === INVESTOWN_CURRENCY;
        const isOriginalValue = isSameCurrency || !showConvertedValue;
        const displayAmount = isOriginalValue ? investownTotalValueSource : investownTotalValue;
        const displayCurrency = isOriginalValue ? INVESTOWN_CURRENCY : currencyCode;

        const valueContent = (
            <>
                {!isOriginalValue ? '≈ ' : ''}
                <ValueAmount amount={displayAmount} nullValue={0} currency={displayCurrency} mode="pure" hidable />
            </>
        );

        return (
            <div className="flex items-center justify-between gap-xsmall">
                <div className="flex items-center gap-[0.8rem]">
                    <IconInvestown />
                    <div className="max-w-[15rem] overflow-hidden text-ellipsis whitespace-nowrap text-small font-semibold opacity-80">
                        Investown
                    </div>
                </div>
                <strong className="text-small">
                    {!isSameCurrency && onToggleValueDisplay ? (
                        <Tooltip content={t('layout.forms.asset.personal.toggleDisplayTooltip')} position="top-right">
                            <button
                                type="button"
                                onClick={onToggleValueDisplay}
                                className="[all:unset] inline-flex cursor-pointer items-center gap-[0.3rem] rounded-[0.4rem] hover:opacity-85 hover:underline hover:decoration-dotted hover:underline-offset-[0.2rem] focus-visible:outline focus-visible:outline-[0.2rem] focus-visible:outline-offset-[0.1rem] focus-visible:outline-border"
                            >
                                {valueContent}
                            </button>
                        </Tooltip>
                    ) : (
                        valueContent
                    )}
                </strong>
            </div>
        );
    },
);
